import React from 'react'

import { LntInput, LntInputNumber, LntRangeDatePicker, LntSelect } from '@app/components/base'

import { toDayjs } from '@app/utils/filters/adapters'
import { getFormattedDate } from '@app/utils/date'
import { isEmptyString, isNullable } from '@app/utils'

import { dateFormat } from '@app/constants'
import { columnSearchSchemaOptionType } from '@app/hooks/useTableSearch/сonstants'

import { commonControlProps } from './constants'

import type { ColumnGroup } from '@app/components/base/Table/types'
import type { LntRangeValue } from '@app/components/base/Form/Input/DatePicker/Range/types'
import type { ObjectValuesType } from '@app/types'
import type { MouseEvent, ChangeEvent } from 'react'
import type {
  Select,
  DateRange,
  UseTableSearchParams,
  ControlComponentOptions,
  ColumnTableSearchSchema,
  ColumnTableSearchSchemaOption
} from './types'

export const initializeColumnsSearch = <ColumnsType = unknown,>({
  columns,
  columnsSearchSchema
}: UseTableSearchParams<ColumnsType>): ColumnTableSearchSchema<ColumnsType> => {
  return columns.reduce((resultColumnsSearchSchema, column) => {
    const columnKey = column.key as keyof ColumnsType
    const title = (resultColumnsSearchSchema[columnKey]?.title ?? column.title) as string

    resultColumnsSearchSchema[columnKey] = {
      ...(columnsSearchSchema[columnKey] as ColumnTableSearchSchemaOption),
      title
    }

    if ((column as ColumnGroup<ColumnsType>)?.children) {
      resultColumnsSearchSchema = {
        ...resultColumnsSearchSchema,
        ...initializeColumnsSearch({ columns: (column as ColumnGroup<ColumnsType>)?.children, columnsSearchSchema })
      }
    }

    return resultColumnsSearchSchema
  }, columnsSearchSchema)
}

export const initializeControlComponent = ({
  tableSearchOption,
  columnSearchOption,
  updateTableSearchMap
}: ControlComponentOptions): JSX.Element | undefined => {
  const { type, controlProps } = columnSearchOption

  const handleClickPlug = (event: MouseEvent): void => event.stopPropagation()

  if (!tableSearchOption && controlProps?.defaultValue) {
    // @ts-expect-error
    updateTableSearchMap({ type, value: controlProps.defaultValue })
  }

  switch (type) {
    case columnSearchSchemaOptionType.INPUT: {
      const defaultValue = tableSearchOption?.value ?? controlProps?.defaultValue

      const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target

        if (isEmptyString(value)) return updateTableSearchMap(null)

        updateTableSearchMap({ type, value })
      }

      return (
        <LntInput
          onClick={ handleClickPlug }
          { ...controlProps }
          { ...commonControlProps }
          defaultValue={ defaultValue as string }
          onChange={ changeHandler }
        />
      )
    }

    case columnSearchSchemaOptionType.NUMERIC_INPUT: {
      const defaultValue = tableSearchOption?.value ?? controlProps?.defaultValue

      const changeHandler = (value: number | null): void => {
        if (isNullable(value)) return updateTableSearchMap(null)

        updateTableSearchMap({ type, value })
      }

      return (
        <LntInputNumber
          onClick={ handleClickPlug }
          { ...controlProps }
          { ...commonControlProps }
          defaultValue={ defaultValue as number }
          onChange={ changeHandler }
        />
      )
    }

    case columnSearchSchemaOptionType.DATE_RANGE: {
      const value = tableSearchOption?.value as DateRange | undefined
      const defaultValue = value
        ? [
            toDayjs(value.from, controlProps?.format as ObjectValuesType<typeof dateFormat>),
            toDayjs(value.to, controlProps?.format as ObjectValuesType<typeof dateFormat>)
          ]
        : controlProps?.defaultValue

      const changeHandler = (rangeValue: LntRangeValue): void => {
        const [before, after] = rangeValue ?? [null, null]

        const format: ObjectValuesType<typeof dateFormat> =
          (controlProps?.format as ObjectValuesType<typeof dateFormat>) ?? dateFormat.DD_MM_YYYY
        const from = getFormattedDate(before, format)
        const to = getFormattedDate(after, format)

        if (isNullable(rangeValue)) return updateTableSearchMap(null)

        updateTableSearchMap({ type, value: { from, to, format } })
      }

      return (
        <LntRangeDatePicker
          onClick={ handleClickPlug }
          { ...controlProps }
          { ...commonControlProps }
          defaultValue={ defaultValue as LntRangeValue }
          onChange={ changeHandler }
        />
      )
    }

    // TODO [MULTI_SELECT]: доработать, когда нужно будет использовать множественный select в поиске по таблице
    // case columnSearchSchemaOptionType.MULTI_SELECT: {
    //   const value = tableSearchOption?.value as Select<string[]> | undefined
    //   const defaultValue = value?.selected ?? controlProps?.defaultValue
    //
    //   const changeHandler = (selectValue?: string[]): void => {
    //     updateTableSearchMap(selectValue?.length ? { type, value: { selected: selectValue } } : null)
    //   }
    //
    //   return (
    //     <LntSelect<string[]>
    //       onClick={ handleClickPlug }
    //       { ...controlProps }
    //       { ...commonControlProps }
    //       showArrow
    //       strictValue
    //       mode="multiple"
    //       defaultValue={ defaultValue as string[] }
    //       onClear={ () => changeHandler([]) }
    //       onChange={ changeHandler }
    //     />
    //   )
    // }

    case columnSearchSchemaOptionType.SELECT: {
      const value = tableSearchOption?.value as Select<string> | undefined
      const defaultValue = value?.selected ?? controlProps?.defaultValue

      const changeHandler = (selected?: string): void => {
        if (!selected?.length) return updateTableSearchMap(null)

        updateTableSearchMap({ type, value: { selected } })
      }

      return (
        <LntSelect<string>
          onClick={ handleClickPlug }
          { ...controlProps }
          { ...commonControlProps }
          showArrow
          defaultValue={ defaultValue as string }
          onChange={ changeHandler }
        />
      )
    }

    case columnSearchSchemaOptionType.NUMERIC_SELECT: {
      const value = tableSearchOption?.value as Select<number> | undefined
      const defaultValue = value?.selected ?? controlProps?.defaultValue

      const changeHandler = (selected?: number): void => {
        if (isNullable(selected)) return updateTableSearchMap(null)

        updateTableSearchMap({ type, value: { selected } })
      }

      return (
        <LntSelect<number>
          onClick={ handleClickPlug }
          { ...controlProps }
          { ...commonControlProps }
          showArrow
          defaultValue={ defaultValue as number }
          onChange={ changeHandler }
        />
      )
    }
  }
}
